import React, { useEffect, useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import api from '@/services/api.service';
import {
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Spin,
  Form,
  Input,
  Select,
  DatePicker,
  App,
  Tabs,
  Descriptions,
  Divider,
  Table,
  Radio,
  Switch,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CommentOutlined,
  SaveOutlined,
  SendOutlined,
  CheckCircleOutlined,
  PrinterOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import contractReviewService, { type ContractReview } from '@/services/contractReview.service';
import { buildContractReviewPrintHtml } from '@/utils/contractReviewPrintHtml';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { approvalService } from '@/services/approval.service';
import { ENTITY_TYPE_CONTRACT_REVIEW } from '@/constants/approval-entity-types';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import {
  buildAmendmentsSectionDefaults,
  formatContractReviewSectionTitle,
  getContractReviewVisibleSectionKeys,
  getDefaultContractReviewItemLabel,
  hasAmendmentsSection,
  isQsfContractReviewSections,
  mergeSectionsPreservingLabels,
  normalizeContractReviewSections,
  sectionProgress,
  type ContractReviewChecklistItem,
} from '@/components/crm/contractReviewChecklistSections';

const { Title, Text } = Typography;

const RESPONSE_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'N/A', label: 'Not Applicable' },
];

const FINAL_OPTIONS = [
  { value: 'approved_to_convert', label: 'Approved to convert to Customer Order' },
  { value: 'approved_with_conditions', label: 'Approved with conditions' },
  { value: 'rejected_needs_correction', label: 'Rejected / needs correction' },
  { value: 'defer', label: 'Defer' },
];

const RISK_OPTIONS = ['Low', 'Medium', 'High', 'Critical'].map((x) => ({ value: x, label: x }));

const ChecklistRemarksCell: React.FC<{ name: Array<string | number>; editable: boolean }> = ({ name, editable }) => {
  const remarks = Form.useWatch(name);
  const [open, setOpen] = useState(false);
  const hasText = Boolean(String(remarks || '').trim());
  const show = open || hasText || editable;

  if (!show) {
    return (
      <Tooltip title="Add remark">
        <Button type="text" size="small" icon={<CommentOutlined />} onClick={() => setOpen(true)} />
      </Tooltip>
    );
  }

  return (
    <Form.Item name={name} style={{ marginBottom: 0, width: '100%' }}>
      <Input
        size="small"
        placeholder="Remark"
        readOnly={!editable}
        onBlur={(e) => {
          if (!editable) return;
          if (!String(e.target.value || '').trim()) setOpen(false);
        }}
      />
    </Form.Item>
  );
};

const ContractReviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [transferOpen, setTransferOpen] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ['contract-review', id],
    queryFn: () => contractReviewService.get(id!),
    enabled: !!id,
  });

  const { data: approvalSummary } = useQuery({
    queryKey: ['approval-summary', ENTITY_TYPE_CONTRACT_REVIEW, id],
    queryFn: () => approvalService.getSummary(ENTITY_TYPE_CONTRACT_REVIEW, id!),
    enabled: !!id && !!row?.approval_instance_id,
  });

  const { data: revenueServiceTypesCr = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body)
        ? body
        : Array.isArray((body as { data?: unknown })?.data)
          ? (body as { data: { id: string; service_code: string; service_name: string }[] }).data
          : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const crRstLabel = useMemo(() => {
    const rid = row?.revenue_service_type_id;
    if (!rid) return '—';
    const r = revenueServiceTypesCr.find((x) => x.id === rid);
    return r ? `${r.service_code} — ${r.service_name}` : String(rid);
  }, [row?.revenue_service_type_id, revenueServiceTypesCr]);
  const crRstSelectOptions = useMemo(
    () => revenueServiceTypesCr.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypesCr]
  );

  const persistedSections = useMemo(
    () => normalizeContractReviewSections(row?.sections as Record<string, unknown> | undefined),
    [row?.sections]
  );

  const isQsf = useMemo(
    () => isQsfContractReviewSections(row?.sections) || isQsfContractReviewSections(persistedSections),
    [row?.sections, persistedSections]
  );

  useEffect(() => {
    if (!row) return;
    form.setFieldsValue({
      review_date: row.review_date ? dayjs(row.review_date) : undefined,
      remarks: row.remarks,
      overall_risk_rating: row.overall_risk_rating || undefined,
      final_recommendation: row.final_recommendation || undefined,
      conditions_text: row.conditions_text,
      revenue_service_type_id: row.revenue_service_type_id || undefined,
      amendments_applicable: hasAmendmentsSection(persistedSections),
      sections: persistedSections,
    });
  }, [row, form, persistedSections]);

  const editable = useMemo(() => {
    if (!row) return false;
    return row.review_status === 'Draft' || row.review_status === 'Returned for Correction';
  }, [row]);

  const watchedSections = Form.useWatch('sections', form);
  const amendmentsApplicable = Form.useWatch('amendments_applicable', form);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const v = await form.validateFields();
      const nextSections = (v.sections || {}) as Record<string, ContractReviewChecklistItem[]>;
      const includeAmendments = Boolean(v.amendments_applicable);
      if (includeAmendments && !Array.isArray(nextSections.review_of_amendments)) {
        nextSections.review_of_amendments = buildAmendmentsSectionDefaults();
      }
      const sections = mergeSectionsPreservingLabels(persistedSections, nextSections, {
        includeAmendments,
      });
      return contractReviewService.update(id!, {
        review_date: v.review_date ? v.review_date.format('YYYY-MM-DD') : undefined,
        remarks: v.remarks,
        sections,
        overall_risk_rating: v.overall_risk_rating,
        final_recommendation: v.final_recommendation,
        conditions_text: v.conditions_text,
        revenue_service_type_id: v.revenue_service_type_id ?? null,
      });
    },
    onSuccess: () => {
      message.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
    },
    onError: (e: any) => message.error(e?.message || e?.response?.data?.message || 'Save failed'),
  });

  const submitMutation = useMutation({
    mutationFn: () => contractReviewService.submit(id!),
    onSuccess: () => {
      message.success('Submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
      queryClient.invalidateQueries({ queryKey: ['approval-summary', ENTITY_TYPE_CONTRACT_REVIEW, id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const ackMutation = useMutation({
    mutationFn: () => contractReviewService.acknowledgeConditions(id!),
    onSuccess: () => {
      message.success('Conditions acknowledged');
      queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });

  const handlePrint = () => {
    if (!row) return;
    const w = window.open('', '_blank');
    if (!w) {
      message.error('Allow pop-ups to print');
      return;
    }
    w.document.write(buildContractReviewPrintHtml(row, { revenueServiceTypeLabel: crRstLabel }));
    w.document.close();
    w.focus();
    w.print();
  };

  const cancelMutation = useMutation({
    mutationFn: () => contractReviewService.cancel(id!),
    onSuccess: () => {
      message.success('Cancelled');
      queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
      queryClient.invalidateQueries({ queryKey: ['contract-reviews-by-quotation', row?.quotation_id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });

  const createReplacementMutation = useMutation({
    mutationFn: (quotationId: string) => contractReviewService.createFromQuotation(quotationId),
    onSuccess: (cr) => {
      message.success(`New Contract Review ${cr.review_number} created`);
      queryClient.invalidateQueries({ queryKey: ['contract-reviews-by-quotation', cr.quotation_id] });
      navigate(`/growth/contract-reviews/${cr.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed to create review'),
  });

  const quotationSummary = row?.quotation as
    | (ContractReview['quotation'] & {
        grand_total?: number;
        currency?: string;
        payment_terms?: string;
      })
    | null
    | undefined;

  if (isLoading || !row) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        {id ? <AiPageContextSync module="contracts" docType="ContractReview" docId={id} /> : null}
        <Spin />
      </div>
    );
  }

  const visibleSectionKeys = getContractReviewVisibleSectionKeys(
    {
      ...persistedSections,
      ...(watchedSections && typeof watchedSections === 'object' ? watchedSections : {}),
    },
    Boolean(amendmentsApplicable)
  );

  const sectionTabs = visibleSectionKeys.map((key) => {
    const persistedList =
      persistedSections[key] ||
      (key === 'review_of_amendments' ? buildAmendmentsSectionDefaults() : []);
    // Form.useWatch only materializes registered fields (response/remarks) and drops `label`.
    const watchedList =
      Array.isArray(watchedSections?.[key]) && watchedSections[key].length
        ? watchedSections[key]
        : null;
    const list = (watchedList || persistedList).map((item, idx) => {
      const persistedItem = persistedList[idx] || {};
      const label =
        String(item?.label || '').trim() ||
        String(persistedItem?.label || '').trim() ||
        getDefaultContractReviewItemLabel(key, idx);
      return {
        ...(persistedItem || {}),
        ...(item || {}),
        label,
        response: item?.response ?? persistedItem?.response ?? 'N/A',
        remarks: item?.remarks ?? persistedItem?.remarks ?? '',
      } as ContractReviewChecklistItem;
    });
    const progress = sectionProgress(list);
    const columns: ColumnsType<{ key: number; label: string }> = [
      {
        title: 'Item',
        dataIndex: 'label',
        key: 'label',
        render: (label: string) => <Text>{label || '—'}</Text>,
      },
      {
        title: 'Response',
        key: 'response',
        width: 320,
        render: (_, record) => (
          <Form.Item name={['sections', key, record.key, 'response']} style={{ marginBottom: 0 }}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              size="small"
              disabled={!editable}
              options={RESPONSE_OPTIONS}
              onChange={(e) => {
                if (e.target.value !== 'Yes') return;
                const remarksPath = ['sections', key, record.key, 'remarks'] as const;
                const current = String(form.getFieldValue(remarksPath) ?? '').trim();
                if (!current) {
                  form.setFieldValue(remarksPath, 'Reviewed');
                }
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: 'Remarks',
        key: 'remarks',
        width: 220,
        render: (_, record) => (
          <ChecklistRemarksCell name={['sections', key, record.key, 'remarks']} editable={editable} />
        ),
      },
    ];
    return {
      key,
      label: (
        <span>
          {formatContractReviewSectionTitle(key)}{' '}
          <Tag style={{ marginInlineStart: 4 }}>
            {progress.complete}/{progress.total}
          </Tag>
        </span>
      ),
      children: (
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          columns={columns}
          dataSource={list.map((item, idx) => ({
            key: idx,
            label: item?.label || getDefaultContractReviewItemLabel(key, idx) || '—',
          }))}
        />
      ),
      forceRender: true,
    };
  });

  return (
    <div style={{ padding: 24 }}>
      {id ? <AiPageContextSync module="contracts" docType="ContractReview" docId={id} /> : null}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/contract-reviews')}>
              Back
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {row.review_number}
            </Title>
            <Tag>{row.review_status}</Tag>
            <Tag color="blue">{row.approval_status}</Tag>
            {approvalSummary?.instance?.status === 'pending_approval' ? (
              <PendingApproverText
                compact
                prefix="Next approver"
                pendingApproverLabel={approvalSummary.pendingApproverLabel}
                pendingApprovers={approvalSummary.pendingApprovers}
              />
            ) : null}
            {row.is_stale ? <Tag color="red">Stale</Tag> : null}
          </Space>
          <Space wrap>
            {editable ? (
              <>
                <Button icon={<SaveOutlined />} onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
                  Save draft
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => submitMutation.mutate()}
                  loading={submitMutation.isPending}
                >
                  Submit for approval
                </Button>
              </>
            ) : null}
            {row.review_status === 'Approved' &&
            row.final_recommendation === 'approved_with_conditions' &&
            !row.conditions_acknowledged_at ? (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => ackMutation.mutate()}>
                Acknowledge conditions
              </Button>
            ) : null}
            {editable && row.review_status === 'Draft' ? (
              <Button danger loading={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                Cancel review
              </Button>
            ) : null}
            {row.is_stale && !row.sales_order_id ? (
              <>
                <Button
                  type="primary"
                  loading={createReplacementMutation.isPending}
                  onClick={() => createReplacementMutation.mutate(row.quotation_id)}
                >
                  Create new Contract Review
                </Button>
                <Button danger loading={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                  Cancel stale review
                </Button>
              </>
            ) : null}
            <Button onClick={() => navigate(`/growth/quotations/${row.quotation_id}`)}>Open quotation</Button>
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
            <ReportDocumentActions
              reportKey="contract-review"
              params={{ id: row.id }}
              documentId={row.id}
              sendPermissions={['crm.contract_review.edit', 'growth.view']}
            />
            <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
              Transfer team
            </Button>
          </Space>
        </Space>

        <SalesTeamTransferModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          entityType="ContractReview"
          entityId={row.id}
          entityLabel={row.review_number}
          currentTeamName={row.sales_team_name}
          onSuccess={() => queryClient.invalidateQueries()}
        />
        <TransferHistoryPanel entityType="ContractReview" entityId={row.id} />

        {row.stale_reason ? (
          <Card size="small" type="inner">
            <Text type="danger">{row.stale_reason}</Text>
          </Card>
        ) : null}

        <Card size="small" title="Header">
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="Quotation">
              {row.quotation?.quotation_number ? (
                <Space direction="vertical" size={0}>
                  <Button
                    type="link"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => navigate(`/growth/quotations/${row.quotation_id}`)}
                  >
                    {row.quotation.quotation_number}
                  </Button>
                  {row.quotation.revision_number != null || row.quotation_revision_number_snapshot != null ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Revision{' '}
                      {displayQuotationRevisionLabel(
                        row.quotation.revision_number ?? row.quotation_revision_number_snapshot
                      )}{' '}
                      (frozen row)
                    </Text>
                  ) : null}
                  {row.quotation.quotation_name ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.quotation.quotation_name}
                    </Text>
                  ) : null}
                  {row.quotation.status ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Status: {row.quotation.status}
                    </Text>
                  ) : null}
                </Space>
              ) : (
                <Space direction="vertical" size={0}>
                  <Text>—</Text>
                  <Text type="secondary" copyable={{ text: row.quotation_id }} style={{ fontSize: 12 }}>
                    ID: {row.quotation_id}
                  </Text>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Sales team">{row.sales_team_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Review date">{String(row.review_date).slice(0, 10)}</Descriptions.Item>
            <Descriptions.Item label="Approved at">{row.approved_at ? String(row.approved_at) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Conditions ack">
              {row.conditions_acknowledged_at ? String(row.conditions_acknowledged_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Revenue service type">{crRstLabel}</Descriptions.Item>
            {quotationSummary?.grand_total != null ? (
              <Descriptions.Item label="Quoted value">
                {Number(quotationSummary.grand_total).toLocaleString()} {quotationSummary.currency || ''}
              </Descriptions.Item>
            ) : null}
            {quotationSummary?.payment_terms ? (
              <Descriptions.Item label="Payment terms">{quotationSummary.payment_terms}</Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>

        {row.approval_instance_id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_CONTRACT_REVIEW}
            entityId={row.id}
            onApproveViaDocument={async (remarks) => {
              await contractReviewService.approve(row.id, remarks);
              queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
            }}
            onRejectViaDocument={async (payload) => {
              await contractReviewService.rejectUniversal(row.id, payload);
              queryClient.invalidateQueries({ queryKey: ['contract-review', id] });
            }}
            extraInvalidateQueryKeys={[
              ['contract-review', id],
              ['approval-summary', ENTITY_TYPE_CONTRACT_REVIEW, id],
            ]}
          />
        ) : null}

        <Card size="small" title="Review body">
          <Form form={form} layout="vertical">
            <Form.Item name="review_date" label="Review date">
              <DatePicker style={{ width: '100%' }} disabled={!editable} />
            </Form.Item>
            <Form.Item name="revenue_service_type_id" label="Revenue service type">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={!editable}
                placeholder="Select type"
                options={crRstSelectOptions}
              />
            </Form.Item>
            <Form.Item name="overall_risk_rating" label="Overall risk rating" rules={[{ required: editable }]}>
              <Select options={RISK_OPTIONS} placeholder="Select" disabled={!editable} />
            </Form.Item>
            <Form.Item name="final_recommendation" label="Final recommendation" rules={[{ required: editable }]}>
              <Select options={FINAL_OPTIONS} placeholder="Select" disabled={!editable} />
            </Form.Item>
            <Form.Item name="conditions_text" label="Conditions (required if Approved with conditions)">
              <Input.TextArea rows={3} disabled={!editable} />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={2} disabled={!editable} />
            </Form.Item>
            <Divider orientation="left">Review sections</Divider>
            {isQsf ? (
              <Form.Item
                name="amendments_applicable"
                label="PO amendment occurred after project number issue"
                valuePropName="checked"
                style={{ marginBottom: 12 }}
              >
                <Switch
                  disabled={!editable}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  onChange={(checked) => {
                    if (checked) {
                      const existing = form.getFieldValue(['sections', 'review_of_amendments']);
                      if (!Array.isArray(existing) || !existing.length) {
                        form.setFieldValue(
                          ['sections', 'review_of_amendments'],
                          buildAmendmentsSectionDefaults()
                        );
                      }
                    } else {
                      form.setFieldValue(['sections', 'review_of_amendments'], undefined);
                    }
                  }}
                />
              </Form.Item>
            ) : null}
            <Tabs items={sectionTabs} />
          </Form>
        </Card>
        <InheritedAttachmentsCard
          trailDomain="sales"
          entityType="ContractReview"
          entityId={row?.id ?? id}
          style={{ marginTop: 0 }}
        />
      </Space>
    </div>
  );
};

export default ContractReviewDetailPage;
